import { Injectable } from '@angular/core';
import { ALGOLIA_CONFIG } from 'src/app/core/config/algolia.config';

// Import search-insights
declare const aa: any;

@Injectable({
  providedIn: 'root'
})
export class AlgoliaInsightsService {
  private readonly APP_ID = ALGOLIA_CONFIG.appId;
  private readonly API_KEY = ALGOLIA_CONFIG.searchApiKey;
  private userToken: string | null = null;

  constructor() {
    this.initializeInsights();
  }

  /**
   * Initialize Algolia Insights with app credentials
   */
  private initializeInsights(): void {
    if (typeof aa !== 'undefined') {
      aa('init', {
        appId: this.APP_ID,
        apiKey: this.API_KEY,
        useCookie: true
      });

      this.userToken = this.getUserToken();
      if (this.userToken) {
        aa('setUserToken', this.userToken);
      }
    }
  }

  /**
   * Get or generate a unique user token for tracking
   */
  private getUserToken(): string {
    const STORAGE_KEY = 'algolia_user_token';
    let token = localStorage.getItem(STORAGE_KEY);

    if (!token) {
      token = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(STORAGE_KEY, token);
    }

    return token;
  }

  /**
   * Set custom user token (e.g., when user logs in)
   */
  setUserToken(token: string): void {
    this.userToken = token;
    if (typeof aa !== 'undefined') {
      aa('setUserToken', token);
    }
    localStorage.setItem('algolia_user_token', token);
  }

  /**
   * Track when a user clicks on a search result
   */
  clickedObjectIDsAfterSearch(params: {
    eventName: string;
    index: string;
    queryID: string;
    objectIDs: string[];
    positions: number[];
  }): void {
    if (!this.userToken || typeof aa === 'undefined') {
      return;
    }

      aa('clickedObjectIDsAfterSearch', {
        userToken: this.userToken,
        eventName: params.eventName,
        index: params.index,
        queryID: params.queryID,
        objectIDs: params.objectIDs,
        positions: params.positions
      });
  }

  /**
   * Track when a user converts (e.g., makes a booking)
   */
  convertedObjectIDsAfterSearch(params: {
    eventName: string;
    index: string;
    queryID: string;
    objectIDs: string[];
  }): void {
    if (!this.userToken || typeof aa === 'undefined') {
      return;
    }

      aa('convertedObjectIDsAfterSearch', {
        userToken: this.userToken,
        eventName: params.eventName,
        index: params.index,
        queryID: params.queryID,
        objectIDs: params.objectIDs
      });
  }

  /**
   * Track clicked items (no query required)
   */
  clickedObjectIDs(params: {
    eventName: string;
    index: string;
    objectIDs: string[];
  }): void {
    if (!this.userToken || typeof aa === 'undefined') {
      return;
    }

      aa('clickedObjectIDs', {
        userToken: this.userToken,
        eventName: params.eventName,
        index: params.index,
        objectIDs: params.objectIDs
      });
  }

  /**
   * Get current user token
   */
  getUserTokenValue(): string | null {
    return this.userToken;
  }
}
